/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useMemo } from 'react'

import { Badge, type BadgeProps } from '@/components/ui'

import { ImportRowEditor } from './ImportRowEditor'
import type { ImportAction, ImportIssue, ImportRowInput, ImportRowResult } from './types'

import { AdminHeaderCells, AdminRowCells, type AdminColumns, useAdminTable } from '../adminTable'

const ACTION: Record<ImportAction, { label: string; variant: BadgeProps['variant'] }> = {
  create: { label: 'New account', variant: 'success' },
  createSso: { label: 'New (SSO)', variant: 'success' },
  grant: { label: 'Give access', variant: 'info' },
  skip: { label: 'Skip', variant: 'ghost' },
  error: { label: 'Needs attention', variant: 'error' },
}

const ISSUE_CLASS: Record<ImportIssue['level'], string> = {
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-base-content/60',
}

export const ActionBadge: React.FC<{ action: ImportAction }> = ({ action }) => (
  <Badge variant={ACTION[action].variant}>{ACTION[action].label}</Badge>
)

interface Props {
  rows: ImportRowInput[]
  verdicts: ImportRowResult[]
  /** Indices to show, after the filter. */
  visible: number[]
  editing: number | null
  onEdit: (index: number | null) => void
  onSave: (index: number, row: ImportRowInput) => void
  onRemove: (index: number) => void
}

const ACTION_ORDER = Object.keys(ACTION) as ImportAction[]
const dash = (text: string) => <span className="text-base-content/40">{text}</span>

export const ImportReviewTable: React.FC<Props> = ({ rows, verdicts, visible, editing, onEdit, onSave, onRemove }) => {
  const columns = useMemo<AdminColumns<number>>(() => {
    // A field with an error is tinted so the admin can see what to fix.
    const fieldCell = (field: ImportIssue['field']) => ({
      tdProps: (index: number) => ({
        className: verdicts[index].issues.some((issue) => issue.level === 'error' && issue.field === field)
          ? 'bg-error/10'
          : undefined,
      }),
    })
    return [
      {
        id: 'row',
        header: '#',
        accessorFn: (index) => index,
        meta: { thClassName: 'w-10', className: 'text-base-content/50' },
        cell: ({ row }) => row.original + 1,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (index) => ACTION_ORDER.indexOf(verdicts[index].action),
        cell: ({ row }) => <ActionBadge action={verdicts[row.original].action} />,
      },
      {
        id: 'email',
        header: 'Email',
        accessorFn: (index) => rows[index].email || undefined,
        meta: fieldCell('email'),
        cell: ({ row }) => rows[row.original].email || dash('—'),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (index) => `${rows[index].firstName} ${rows[index].lastName}`.trim() || undefined,
        meta: fieldCell('firstName'),
        cell: ({ getValue }) => getValue<string | undefined>() ?? dash('—'),
      },
      { id: 'company', header: 'Company', accessorFn: (index) => rows[index].company, meta: fieldCell('company') },
      { id: 'username', header: 'Username', accessorFn: (index) => rows[index].username, meta: fieldCell('username') },
      {
        id: 'dashboard',
        header: 'Dashboard',
        accessorFn: (index) => rows[index].dashboard || undefined,
        meta: fieldCell('dashboard'),
        cell: ({ row }) => {
          const input = rows[row.original]
          const verdict = verdicts[row.original]
          return verdict.dashboardSourceUid
            ? `${input.dashboard} (${verdict.dashboardCount ?? 0})`
            : input.dashboard || dash('Default')
        },
      },
      {
        id: 'notes',
        header: 'Notes',
        // By how much there is to read.
        accessorFn: (index) => verdicts[index].issues.length,
        meta: { className: 'min-w-64' },
        cell: ({ row }) => (
          <ul className="flex flex-col gap-0.5 text-xs">
            {verdicts[row.original].issues.map((issue, i) => (
              <li key={i} className={ISSUE_CLASS[issue.level]}>
                {issue.message}
              </li>
            ))}
          </ul>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { className: 'whitespace-nowrap text-right' },
        cell: ({ row }) => (
          <>
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => onEdit(editing === row.original ? null : row.original)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-xs text-error"
              aria-label={`Remove row ${row.original + 1}`}
              onClick={() => onRemove(row.original)}
            >
              Remove
            </button>
          </>
        ),
      },
    ]
  }, [rows, verdicts, editing, onEdit, onRemove])

  const table = useAdminTable(visible, columns, { getRowId: (index) => String(index) })

  return (
    <div className="overflow-x-auto rounded-lg bg-base-100 shadow">
      <table className="table table-sm">
        <thead>
          <tr>
            <AdminHeaderCells table={table} />
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((tableRow) => {
            const index = tableRow.original
            return (
              <React.Fragment key={index}>
                <tr data-testid={`import-row-${index}`}>
                  <AdminRowCells row={tableRow} />
                </tr>
                {editing === index && (
                  <tr>
                    <td colSpan={columns.length}>
                      <ImportRowEditor
                        row={rows[index]}
                        verdict={verdicts[index]}
                        onSave={(updated) => onSave(index, updated)}
                        onCancel={() => onEdit(null)}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
